import { buildMeetingPath } from '../utils/url';

export type StoredMeetingHash = {
  meetingId: string;
  isPending: boolean;
  hash: string;
};

export type PreLoginAction = { type: 'start_meeting' };

/** Manages post-auth redirect URLs via sessionStorage. */
export class AuthRedirectService {
  readonly #keys = {
    meetingHashParams: 'meetingHashParams',
    preLoginUrl: 'preLoginUrl',
    preLoginAction: 'preLoginAction',
  } as const;

  readonly #nonRedirectPaths = ['/', '/close'];

  #skipNextPreLoginUrl = false;

  consumeRedirect(meetingId: string | null): string | null {
    const preLoginRedirect = this.#consumePreLoginRedirect();
    if (preLoginRedirect) {
      return preLoginRedirect;
    }

    if (meetingId === null) {
      this.removeMeetingHashParams();
      return null;
    }

    return this.#consumeMeetingRedirect();
  }

  /** Makes the next `savePreLoginUrl()` call a no-op. The flag is consumed on use. */
  skipNextPreLoginUrl(): void {
    this.#skipNextPreLoginUrl = true;
  }

  /** Called from preLogin OAuth callback. */
  savePreLoginUrl(): void {
    if (this.#skipNextPreLoginUrl) {
      this.#skipNextPreLoginUrl = false;
      return;
    }

    const currentPath = window.location.pathname;
    if (!this.#nonRedirectPaths.includes(currentPath)) {
      sessionStorage.setItem(this.#keys.preLoginUrl, currentPath);
    }
  }

  saveMeetingHashParams(data: StoredMeetingHash): void {
    sessionStorage.setItem(this.#keys.meetingHashParams, JSON.stringify(data));
  }

  removeMeetingHashParams(): void {
    sessionStorage.removeItem(this.#keys.meetingHashParams);
  }

  savePreLoginAction(action: PreLoginAction): void {
    sessionStorage.setItem(this.#keys.preLoginAction, JSON.stringify(action));
  }

  consumePreLoginAction(): PreLoginAction | null {
    const raw = sessionStorage.getItem(this.#keys.preLoginAction);
    if (!raw) {
      return null;
    }

    sessionStorage.removeItem(this.#keys.preLoginAction);
    try {
      return JSON.parse(raw) as PreLoginAction;
    } catch {
      return null;
    }
  }

  getStoredMeetingHash(meetingId: string): string | null {
    const stored = this.#readMeetingHashParams();
    return stored?.meetingId === meetingId ? stored.hash : null;
  }

  #readMeetingHashParams(): StoredMeetingHash | null {
    try {
      const raw = sessionStorage.getItem(this.#keys.meetingHashParams) as string;
      return JSON.parse(raw) as StoredMeetingHash;
    } catch {
      return null;
    }
  }

  #consumePendingHash(): { meetingId: string; hash: string } | null {
    const data = this.#readMeetingHashParams();
    if (!data?.isPending) {
      return null;
    }

    this.saveMeetingHashParams({ ...data, isPending: false });
    return { meetingId: data.meetingId, hash: data.hash };
  }

  #consumePreLoginRedirect(): string | null {
    const preLoginUrl = sessionStorage.getItem(this.#keys.preLoginUrl);
    if (!preLoginUrl) {
      return null;
    }

    sessionStorage.removeItem(this.#keys.preLoginUrl);
    const pending = this.#consumePendingHash();
    return pending ? `${preLoginUrl}#${pending.hash}` : preLoginUrl;
  }

  #consumeMeetingRedirect(): string | null {
    const pending = this.#consumePendingHash();
    return pending ? buildMeetingPath(pending.meetingId, pending.hash) : null;
  }
}

export const authRedirectService = new AuthRedirectService();
